import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/core/authentication/services/auth.service';
import { ClientConfigurationService } from 'src/app/core/client-configuration/client-configuration.service';
import { ConfigSettings } from 'src/app/core/client-configuration/config-settings.enum';
import { DateHelperService } from 'src/app/core/helpers/date-helper/date-helper.service';
import { AppointmentTimeEntry } from 'src/app/core/models/appointment-time-entry.model';
import { ConfigSetting } from 'src/app/core/models/config-settings.model';
import { SyncService } from 'src/app/core/sync/sync.service';
import { TimeEntryService } from 'src/app/core/time-entry/time-entry.service';
import { TimeTrackingFormComponent } from 'src/app/pages/time-tracking-form/time-tracking-form.component';

@Component({
	selector: 'app-time-tracking-list',
	templateUrl: './time-tracking-list.component.html',
	styleUrls: ['./time-tracking-list.component.scss']
})
export class TimeTrackingListComponent {

	pageTitle = 'Time Tracking';

	apptTimeEntries: AppointmentTimeEntry[];
	expandedSections = [] as number[];
	techId: number;
	DisableEditOnCleared: ConfigSetting;
	destroyed$: Subject<boolean>;

	constructor(
		private modalController: ModalController,
		public timeEntryService: TimeEntryService,
		private dateHelper: DateHelperService,
		private auth: AuthService,
		private syncService: SyncService,
		private clientConfig: ClientConfigurationService,
	) { }

	async ionViewWillEnter(): Promise<void> {
		this.destroyed$ = new Subject();
		this.DisableEditOnCleared = await this.clientConfig.getSetting(ConfigSettings.Disable_Edit_TimeEntry_OnCleared_Appt);
		this.updateOnSync();

		this.techId = await this.auth.getCurrentUserId();
		this.setTimeEntries();
	}

	ionViewWillLeave() {
		this.apptTimeEntries = [];
		this.expandedSections = [];
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	async setTimeEntries() {
		const entries = await this.timeEntryService.getAll();
		entries.sort((a, b) => a.apptStart > b.apptStart ? 1 : -1);

		this.apptTimeEntries = entries;
	}

	updateOnSync() {
		this.syncService.isSyncing$
			.pipe(takeUntil(this.destroyed$))
			.subscribe(isSyncing => {
				if (!isSyncing) {
					this.setTimeEntries();
				}
			});
	}

	isVisible(index: number): boolean {
		const foundIndex = this.expandedSections.findIndex(i => i === index);
		return foundIndex === -1 ? false : true;
	}

	getTotalTimeToday(): string {
		const time = this.timeEntryService.getTotalTimeToday(this.apptTimeEntries);

		return `${time.hours}h ${time.minutes}m`;
	}

	getAppointmentTimeEntryTime(apptTimeEntry: AppointmentTimeEntry): string {
		const time = this.timeEntryService.getAppointmentTimeEntryTime(apptTimeEntry);

		return `${time.hours}h ${time.minutes}m`;
	}

	getMinutesFormatted(minutes: number): string {
		const time = this.dateHelper.getHoursMinutesFromMinutes(minutes);

		return `${time.hours}h ${time.minutes}m`;
	}

	onClickToggleSection(index: number): void {
		const foundIndex = this.expandedSections.findIndex(i => i === index);
		if (foundIndex === -1) {
			this.expandedSections.push(index);
			return;
		}

		this.expandedSections.splice(foundIndex, 1);
	}

	async onClickAddTimeEntry(): Promise<void> {
		const modal = await this.modalController.create({
			component: TimeTrackingFormComponent,
			componentProps: {
				mode: 'Add',
				DisableEditOnCleared: this.DisableEditOnCleared,
			},
			cssClass: 'full-screen-modal',
		});

		await modal.present();
		await modal.onDidDismiss();
		await this.setTimeEntries();
	}

}
